// Canvas Layer subcomponent
import type {
  CanvasLayerAPI,
  SketchPointerEvent,
  SketchPointerPhase,
  SketchPointerState,
  SketchStrokeRenderInput,
  SketchToolStyle,
  SketchToolType,
  SketchViewportAPI,
  TransformState,
} from "../../../../types/stage-types.js";
import type { AppMode } from "../../../../utils/layer-manager.js";
import { logger } from "../../../../utils/logger.ts";
import { createSketchViewport } from "./sketch/viewport.js";
import { createSketchModule } from "./sketch/index.js";
import {
  createStrokeRenderer,
  pointsToPath,
  pointsToPolylinePath,
  pointsToSmoothPath,
} from "./sketch/StrokeRenderer.js";
import { createToolManager } from "./sketch/ToolManager.js";
import type { SketchStrokeModel, StrokePoint } from "./sketch/ToolManager.js";
import { createSelectionTool } from "./sketch/tools/SelectionTool.js";

export interface CanvasLayerSketchConfig {
  readonly viewportWidth?: number;
  readonly viewportHeight?: number;
  readonly logPointerEvents?: boolean;
  readonly getModifiers?: () => { readonly shift: boolean; readonly alt: boolean };
  readonly onStrokeCommit?: (stroke: SketchStrokeModel) => void;
  readonly onStrokeOperation?: (
    operation:
      | { op: "stroke.add"; stroke: SketchStrokeModel }
      | { op: "stroke.replace"; stroke: SketchStrokeModel }
      | { op: "stroke.remove"; strokeId: string }
  ) => void;
  readonly onTransformOperation?: (payload: {
    readonly strokeIds: readonly string[];
    readonly transform: {
      readonly translate?: { readonly x: number; readonly y: number };
      readonly rotate?: {
        readonly angle: number;
        readonly origin?: { readonly x: number; readonly y: number };
      };
      readonly scale?: {
        readonly x: number;
        readonly y?: number;
        readonly origin?: { readonly x: number; readonly y: number };
      };
    };
    readonly updatedStrokes: readonly SketchStrokeModel[];
  }) => void;
  readonly onHistoryChange?: (state: {
    readonly canUndo: boolean;
    readonly canRedo: boolean;
  }) => void;
}

export interface CanvasLayerConfig {
  readonly sketch?: CanvasLayerSketchConfig;
}

const DEFAULT_VIEWPORT_WIDTH = 1920;
const DEFAULT_VIEWPORT_HEIGHT = 1080;
export const DEFAULT_BALLPOINT_COLOR = "#000000";
export const DEFAULT_BALLPOINT_WIDTH = 2;

/**
 * Canvas layer orchestrator. Coordinates the sketch viewport and delegates
 * pointer handling to the sketch submodules.
 */
export const createCanvasLayer = (
  container: HTMLElement,
  config: CanvasLayerConfig = {}
): CanvasLayerAPI => {
  let isDestroyed = false;
  let currentMode: AppMode = "edit";

  const sketchConfig = config.sketch ?? {};
  const host = document.createElement("div");
  host.className = "canvas-layer__root";

  const sketchWrapper = document.createElement("div");
  sketchWrapper.className = "canvas-layer__sketch-wrapper";
  host.appendChild(sketchWrapper);

  container.innerHTML = "";
  container.appendChild(host);

  const viewport = createSketchViewport(sketchWrapper, {
    width: sketchConfig.viewportWidth ?? DEFAULT_VIEWPORT_WIDTH,
    height: sketchConfig.viewportHeight ?? DEFAULT_VIEWPORT_HEIGHT,
  });

  const selectionActionsHost = host;

  const sketchModule = createSketchModule({
    viewport,
    surface: sketchWrapper,
    logPointerEvents: sketchConfig.logPointerEvents ?? false,
  });
  const updateViewportSize = (): void => {
    const rect = viewport.element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      viewport.setLogicalSize({
        width: rect.width,
        height: rect.height,
      });
    }
  };
  updateViewportSize();
  const resizeObserver =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => {
          updateViewportSize();
        })
      : null;
  resizeObserver?.observe(sketchWrapper);
  const strokeRenderer = createStrokeRenderer(
    viewport.contentRoot,
    sketchConfig.logPointerEvents ?? false
  );
  const renderedStrokeIds = new Set<string>();
  let currentSketchTool: SketchToolType | null = null;
  const getModifiers = sketchConfig.getModifiers ?? (() => ({ shift: false, alt: false }));
  const toolManager = createToolManager({
    renderer: strokeRenderer,
    defaultColor: DEFAULT_BALLPOINT_COLOR,
    defaultWidth: DEFAULT_BALLPOINT_WIDTH,
    logDebug: sketchConfig.logPointerEvents ?? false,
    getModifiers,
    onStrokeAdded: (stroke) => {
      renderedStrokeIds.add(stroke.id);
      sketchConfig.onStrokeOperation?.({ op: "stroke.add", stroke });
    },
    onStrokeReplaced: (stroke) => {
      renderedStrokeIds.add(stroke.id);
      sketchConfig.onStrokeOperation?.({ op: "stroke.replace", stroke });
    },
    onStrokeRemoved: (strokeId) => {
      renderedStrokeIds.delete(strokeId);
      sketchConfig.onStrokeOperation?.({ op: "stroke.remove", strokeId });
    },
    onHistoryChange: sketchConfig.onHistoryChange,
  });
  toolManager.setActive(false);

  const selectionTool = createSelectionTool({
    root: viewport.contentRoot,
    getStrokes: () => toolManager.getStrokes(),
    getSelectionMode: () => toolManager.getStyle().selectionMode ?? "marquee",
    onSelectionChange: (selection) => {
      toolManager.setSelection(selection);
    },
    onTransformPreview: (strokes) => {
      toolManager.previewStrokeUpdates(strokes);
    },
    onTransformCommit: (before, after, transform) => {
      if (transform) {
        toolManager.commitStrokeUpdates(before, after, { emitEvents: false });
        if (sketchConfig.onTransformOperation) {
          const ids = before.map((stroke) => stroke.id);
          sketchConfig.onTransformOperation({
            strokeIds: ids,
            transform,
            updatedStrokes: after,
          });
        }
        return;
      }
      toolManager.commitStrokeUpdates(before, after, { emitEvents: true });
    },
    onTransformCancel: (strokes) => {
      toolManager.previewStrokeUpdates(strokes);
    },
    onDeleteSelection: (selection) => {
      if (!selection.length) return;
      toolManager.removeStrokes(selection.map((stroke) => stroke.id));
      selectionTool.clearSelection();
    },
    actionsHost: selectionActionsHost,
    getViewportRect: () => viewport.element.getBoundingClientRect(),
    getWrapperRect: () => sketchWrapper.getBoundingClientRect(),
    getLogicalSize: () => viewport.getLogicalSize(),
    getModifiers,
  });

  const unsubscribeToolPointer = sketchModule.onPointerEvent((event) => {
    if (toolManager.getStyle().tool === "marquee") {
      selectionTool.handlePointerEvent(event);
      return;
    }
    toolManager.handlePointerEvent(event);
  });

  const productContainer = container.parentElement as HTMLElement | null;

  const ensureSketchLayout = (isSketch: boolean): void => {
    container.classList.toggle("canvas-layer--sketch-active", isSketch);
    if (productContainer) {
      productContainer.classList.toggle(
        "stage__product-container--sketch",
        isSketch
      );
    }
    if (isSketch) {
      updateViewportSize();
    }
  };

  ensureSketchLayout(false);

  const setMode = (mode: AppMode): void => {
    if (isDestroyed) return;
    if (currentMode === mode) return;

    currentMode = mode;
    const isSketch = mode === "sketch";
    ensureSketchLayout(isSketch);

    if (isSketch) {
      toolManager.setActive(true);
      selectionTool.setActive(currentSketchTool === "marquee");
      sketchModule.activate();
      logger.debug("sketch", "🟣 CanvasLayer activated sketch mode");
    } else {
      toolManager.setActive(false);
      selectionTool.setActive(false);
      sketchModule.deactivate();
      logger.debug("sketch", "⚪️ CanvasLayer exited sketch mode", { mode });
    }
  };

  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;
    container.style.visibility = visible ? "visible" : "hidden";
  };

  const setTransform = (_transform: TransformState): void => {
    if (isDestroyed) return;
    // Stage transform applied on parent; no-op placeholder.
  };

  const onSketchPointerEvent = (
    listener: (event: SketchPointerEvent) => void
  ): (() => void) => {
    if (isDestroyed) {
      return () => {};
    }
    return sketchModule.onPointerEvent(listener);
  };

  const getSketchViewport = (): SketchViewportAPI | null =>
    isDestroyed ? null : viewport;

  const getPointerState = (): SketchPointerState => ({
    activePointerType: sketchModule.getActivePointerType(),
    isActive: sketchModule.isActive(),
  });

  const setSketchStyle = (style: Partial<SketchToolStyle>): void => {
    if (isDestroyed) return;
    const nextTool =
      style.tool ?? toolManager.getStyle().tool ?? "ballpoint";
    if (nextTool !== currentSketchTool) {
      currentSketchTool = nextTool;
      selectionTool.setActive(nextTool === "marquee");
    }
    toolManager.setStyle(style);
  };

  const getSketchStyle = (): SketchToolStyle | null =>
    isDestroyed ? null : toolManager.getStyle();

  const canUndoSketchStroke = (): boolean =>
    !isDestroyed && toolManager.canUndo();

  const canRedoSketchStroke = (): boolean =>
    !isDestroyed && toolManager.canRedo();

  const commitSketchStroke = (
    stroke: SketchStrokeRenderInput
  ): void => {
    if (isDestroyed) return;
    if (renderedStrokeIds.has(stroke.id)) return;
    const shouldClose =
      stroke.primitive === "rect" ||
      stroke.primitive === "square" ||
      stroke.primitive === "ellipse" ||
      stroke.primitive === "circle";
    const pathData =
      stroke.primitive
        ? pointsToPolylinePath(stroke.points, shouldClose)
        : stroke.pointerType === "pen"
          ? pointsToSmoothPath(stroke.points)
          : pointsToPath(stroke.points);
    if (!pathData) return;
    toolManager.ingestStroke({
      id: stroke.id,
      pointerId: -1,
      pointerType: stroke.pointerType ?? "unknown",
      tool: stroke.tool ?? "ballpoint",
      color: stroke.color,
      width: stroke.width,
      opacity: stroke.opacity ?? 1,
      ...(stroke.splitFromId ? { splitFromId: stroke.splitFromId } : {}),
      ...(stroke.primitive ? { primitive: stroke.primitive } : {}),
      points: (stroke.points ?? []) as unknown as StrokePoint[],
      pathData,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    renderedStrokeIds.add(stroke.id);
  };

  const removeSketchStroke = (strokeId: string): void => {
    if (isDestroyed) return;
    toolManager.removeStroke(strokeId);
    renderedStrokeIds.delete(strokeId);
  };

  const removeSketchStrokes = (strokeIds: readonly string[]): void => {
    if (isDestroyed) return;
    toolManager.removeStrokes(strokeIds);
    strokeIds.forEach((id) => renderedStrokeIds.delete(id));
  };

  const selectAllSketchStrokes = (): void => {
    if (isDestroyed) return;
    const strokes = toolManager.getStrokes();
    toolManager.setSelection(strokes);
    selectionTool.setSelection(strokes);
  };

  const undoSketchStroke = (): SketchStrokeRenderInput | null => {
    if (isDestroyed) return null;
    const stroke = toolManager.undo();
    if (!stroke) return null;
    renderedStrokeIds.clear();
    for (const committed of toolManager.getStrokes()) {
      renderedStrokeIds.add(committed.id);
    }
    selectionTool.setSelection(toolManager.getSelection());
    return {
      id: stroke.id,
      points: stroke.points as unknown as readonly Point[],
      color: stroke.color,
      width: stroke.width,
      opacity: stroke.opacity,
      tool: stroke.tool,
      pointerType: stroke.pointerType,
      ...(stroke.primitive ? { primitive: stroke.primitive } : {}),
    };
  };

  const redoSketchStroke = (): SketchStrokeRenderInput | null => {
    if (isDestroyed) return null;
    const stroke = toolManager.redo();
    if (!stroke) return null;
    renderedStrokeIds.clear();
    for (const committed of toolManager.getStrokes()) {
      renderedStrokeIds.add(committed.id);
    }
    selectionTool.setSelection(toolManager.getSelection());
    return {
      id: stroke.id,
      points: stroke.points as unknown as readonly Point[],
      color: stroke.color,
      width: stroke.width,
      opacity: stroke.opacity,
      tool: stroke.tool,
      pointerType: stroke.pointerType,
      ...(stroke.primitive ? { primitive: stroke.primitive } : {}),
    };
  };

  const setSketchStrokes = (
    strokes: readonly SketchStrokeRenderInput[]
  ): void => {
    if (isDestroyed) return;
    toolManager.reset();
    selectionTool.clearSelection();
    renderedStrokeIds.clear();
    for (const stroke of strokes) {
      commitSketchStroke(stroke);
    }
    const committed = strokes.map<SketchStrokeModel>((stroke) => ({
      id: stroke.id,
      pointerId: -1,
      pointerType: stroke.pointerType ?? "unknown",
      tool: stroke.tool ?? "ballpoint",
      color: stroke.color,
      width: stroke.width,
      opacity: stroke.opacity ?? 1,
      ...(stroke.splitFromId ? { splitFromId: stroke.splitFromId } : {}),
      ...(stroke.primitive ? { primitive: stroke.primitive } : {}),
      points: (stroke.points ?? []) as unknown as StrokePoint[],
      pathData: stroke.primitive
        ? pointsToPolylinePath(
            stroke.points,
            stroke.primitive === "rect" ||
              stroke.primitive === "square" ||
              stroke.primitive === "ellipse" ||
              stroke.primitive === "circle"
          )
        : (stroke.pointerType ?? "unknown") === "pen"
          ? pointsToSmoothPath(stroke.points)
          : pointsToPath(stroke.points),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }));
    toolManager.setStrokes(committed);
  };

  const updateSketchStrokes = (
    strokes: readonly SketchStrokeRenderInput[]
  ): void => {
    if (isDestroyed) return;
    if (!strokes.length) return;
    const models = strokes.map<SketchStrokeModel>((stroke) => ({
      id: stroke.id,
      pointerId: -1,
      pointerType: stroke.pointerType ?? "unknown",
      tool: stroke.tool ?? "ballpoint",
      color: stroke.color,
      width: stroke.width,
      opacity: stroke.opacity ?? 1,
      ...(stroke.splitFromId ? { splitFromId: stroke.splitFromId } : {}),
      ...(stroke.primitive ? { primitive: stroke.primitive } : {}),
      points: (stroke.points ?? []) as unknown as StrokePoint[],
      pathData: stroke.primitive
        ? pointsToPolylinePath(
            stroke.points,
            stroke.primitive === "rect" ||
              stroke.primitive === "square" ||
              stroke.primitive === "ellipse" ||
              stroke.primitive === "circle"
          )
        : (stroke.pointerType ?? "unknown") === "pen"
          ? pointsToSmoothPath(stroke.points)
          : pointsToPath(stroke.points),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }));
    toolManager.updateStrokes(models);
  };

  const dispatchSketchPointerEvent = (
    phase: SketchPointerPhase,
    event: PointerEvent
  ): void => {
    if (isDestroyed) return;
    sketchModule.processPointerEvent(phase, event, {
      // Stage already manages pointer capture; avoid double capture.
      usePointerCapture: false,
    });
  };

  /**
   * Force-finish any active stroke for the given pointer ID.
   * Used when cleaning up orphaned pointers during rapid stroke succession.
   */
  const forceFinishStroke = (pointerId: number): void => {
    if (isDestroyed) return;
    toolManager.forceFinishStroke(pointerId);
    sketchModule.clearOrphanedPointer(pointerId);
  };

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;

    unsubscribeToolPointer();
    resizeObserver?.disconnect();
    selectionTool.destroy();
    toolManager.destroy();
    strokeRenderer.destroy();
    sketchModule.destroy();
    viewport.destroy();

    ensureSketchLayout(false);
    container.innerHTML = "";
  };

  return {
    setMode,
    setVisible,
    setTransform,
    destroy,
    getSketchViewport,
    onSketchPointerEvent,
    getPointerState,
    dispatchSketchPointerEvent,
    setSketchStyle,
    getSketchStyle,
    setSketchStrokes,
    updateSketchStrokes,
    commitSketchStroke,
    removeSketchStroke,
    removeSketchStrokes,
    selectAllSketchStrokes,
    undoSketchStroke,
    redoSketchStroke,
    canUndoSketchStroke,
    canRedoSketchStroke,
    forceFinishStroke,
  };
};
